import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { FilterMakeIcon } from '../../assets';
import { useFilterContext } from '../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { MakeFilter } from '../../models/Filter';
import BaseFilterComponent from './BaseFilterComponent';
import { StyledButton, StyledSecondaryButton } from './FiltersList.styles';
import { useSelectionFilter } from './hooks/SelectionFilter';
import SelectionFilter, { Value } from './SelectionFilter/SelectionFilter';
import { FilterFooterMobile } from './shared/FilterFooterMobile.styles';

export const AddMakeFilterComponent = () => {
  const { t } = useTranslation();
  const {
    getSelectedFilter,
    handleRemoveMakeFilter,
    filterCountsData,
    handleApplyFilter,
    setOpenFiltersMobile,
    setSelectedFilterTitle,
  } = useFilterContext();
  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);

  const filter = getSelectedFilter(MakeFilter) as MakeFilter;
  const { getFilter, optionValues, onChangeMultiSelectionFnc, getSelectedValues, clearAllSelected } =
    useSelectionFilter({
      arrOptions,
      filter,
      constructor: (makes) => new MakeFilter({ values: makes }),
    });

  React.useEffect(() => {
    setArrOptions(
      filterCountsData?.make?.map((option) => ({
        selected: option.isSelected,
        value: option.key,
        label: option.key,
        data: option.count,
      })),
    );
  }, [filterCountsData]);

  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();
    handleApplyFilter(filter);
    handleRemoveMakeFilter(getSelectedValues());
    /* This setTimeOut is because when setOpenFiltersMobile(false) all the content is unMounted,
       And some effects in useSelectionFilter are not being executed. */
    setTimeout(() => {
      setOpenFiltersMobile(false);
      setSelectedFilterTitle('');
    }, 100);
  };

  return (
    <>
      <SelectionFilter
        values={optionValues}
        onChange={onChangeMultiSelectionFnc}
        multiselect
        searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'make' })}
        displayCount={false}
        reverseCheckboxOrder={true}
        checkboxSizeInPx={24}
      />
      <FilterFooterMobile>
        <StyledSecondaryButton onPress={clearAllSelected}>{t('filters.clearAll')}</StyledSecondaryButton>
        <StyledButton disabled={arrOptions?.length === 0} onPress={applyOnClick}>
          {t('filters.applyFilter')}
        </StyledButton>
      </FilterFooterMobile>
    </>
  );
};

const AddMakeFilter = () => {
  // Dependencies
  const { t } = useTranslation();
  const { getSelectedFilter, handleRemoveMakeFilter, filterCountsData } = useFilterContext();
  const { width } = useWindowDimensions();

  // State
  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);

  // Derived State
  const filter = getSelectedFilter(MakeFilter) as MakeFilter;
  const { getFilter, optionValues, onCancel, onChangeMultiSelectionFnc, getSelectedValues, clearAllSelected } =
    useSelectionFilter({
      arrOptions,
      filter,
      constructor: (makes) => new MakeFilter({ values: makes }),
    });

  // Effects
  React.useEffect(() => {
    setArrOptions(
      filterCountsData?.make?.map((option) => ({
        selected: option.isSelected,
        value: option.key,
        label: option.key,
        data: option.count,
      })),
    );
  }, [filterCountsData]);

  return (
    <>
      <BaseFilterComponent
        getFilter={getFilter}
        icon={<FilterMakeIcon />}
        title={t('filters.make')}
        onCancel={onCancel}
        onClearAll={clearAllSelected}
        onApply={() => {
          handleRemoveMakeFilter(getSelectedValues());
        }}
        inUse={!!filter}
        storedFilter={filter}
        disableFilterButton={arrOptions?.length === 0}
      >
        <SelectionFilter
          values={optionValues}
          onChange={onChangeMultiSelectionFnc}
          multiselect
          searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'make' })}
          displayCount={!inMobile(width)}
          reverseCheckboxOrder={inMobile(width)}
          checkboxSizeInPx={inMobile(width) ? 24 : undefined}
        />
      </BaseFilterComponent>
    </>
  );
};

export default AddMakeFilter;
